import type { ReactNode } from 'react'
import { pluralize } from '@/utils/string.utils'

/**
 * Screen reader text for ticketing and checkout: live-region announcements, aria-labels, titles and alt text
 */
export const TICKETING_A11Y = {
  cartTotal: (total: string) => `Your cart total is ${total}`,
  selectedDate: (date: string) => `You have selected ${date}`,
  displayingTimeslots: (timeOfDay: string) => `Displaying ${timeOfDay} timeslots`,
  selectTimeslot: (time: string, remaining: string) => `Select ${time} timeslot. ${remaining}`,
  displayTimeslots: (timeOfDay: string) => `Display ${timeOfDay} timeslots`,
  ticketsAdded: (quantity: number, title: string) => `${quantity} ${title} ${pluralize(quantity, 'ticket')}`,
  ticketPrice: (title: string) => `${title} ticket price`,
  addTicket: (title: string) => `Add a ${title} ticket`,
  ticketQuantity: (title: string) => `${title} ticket quantity`,
  removeTicket: (title: string) => `Remove a ${title} ticket`,
  removeTicketsFromCart: (quantity: number, title: string) => (
    `Remove ${quantity} ${title} ${pluralize(quantity, 'ticket')} from cart`
  ),
  orderSummary: 'order summary',
  clearAllTickets: 'Clear all tickets from cart',
  goToCart: 'go to cart',
  edit: (title: string) => `Edit ${title}`,
  toggleCollapsed: 'Toggle collapsed content',
  showTaxInfo: 'Show tax information',
  warningIcon: 'exclamation triangle',
}

/**
 * Screen reader text for events pages and site chrome: live-region announcements, aria-labels, titles and alt text
 */
export const EVENTS_A11Y = {
  searchResults: (count: number, singleMatchTitle: string) => (
    `${count} search ${pluralize(count, 'result')}. ${singleMatchTitle}`
  ),
  loggedInUserMenu: 'Logged in user menu',
  userProfile: 'User profile',
  backToEventsList: 'back to events list',
  goBack: 'Go Back',
  heroDivider: 'Hero Divider',
  venueLocation: (place: Nullable<string>) => `Venue Location ${place}`,
  addressText: 'address text',
  locationPin: 'Location Pin',
  informationIcon: 'Information Icon',
  questionMarkIcon: 'Question mark Icon',
  exclamationIcon: 'Exclamation Icon',
  seatedEventIcon: 'Seated Event Icon',
  infinityIcon: 'Infinity Icon',
  displayAllEvents: 'Display all events',
  displayMusicEvents: 'Display music events',
  displayCommunityEvents: 'Display community events',
  musicCategoryIcon: 'Music category icon',
  communityCategoryIcon: 'Community category icon',
  clearSearch: 'Clear search',
  submitSearch: 'Submit search',
  certifiedBCorp: 'Certified B Corporation',
  mwLogo: 'Meow Wolf Logo',
  privacyIcon: 'privacy icon',
  previousSlide: 'Previous Slide Button',
  nextSlide: 'Next Slide Button',
  visitExhibition: (exhibitionName: string, cityName: string) => `Visit ${exhibitionName} ${cityName}`,
  exhibitionLogo: 'Exhibition Logo',
  logo: (name: ReactNode) => `${name} logo`,
  angleDown: 'Angle Down',
  ticketsLandingPage: 'Tickets landing page',
  linkToTicketsPage: 'Link to tickets page',
  buyAdmissionTicketsFor: (orgAndCity: string) => `Click here to buy admission tickets for ${orgAndCity}`,
  buyTicketsFor: (orgAndCity: string) => `Click here to buy tickets for ${orgAndCity}`,
  exhibitTickets: (orgAndCity: string) => `${orgAndCity} exhibit tickets`,
  eventTickets: (orgAndCity: string) => `${orgAndCity} event tickets`,
}
